import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

interface Toast {
  visible: boolean;
  message: string;
  error: boolean;
}

export default function LandingPage() {
  const [email1, setEmail1] = useState('');
  const [email2, setEmail2] = useState('');
  const [loading1, setLoading1] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [inputError1, setInputError1] = useState(false);
  const [inputError2, setInputError2] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [toast, setToast] = useState<Toast>({ visible: false, message: '', error: false });

  const showToast = (message: string, error = false, duration = 4000) => {
    setToast({ visible: true, message, error });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), duration);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('confirmed') === 'true') {
      showToast('✓ Email confirmat! Te vom contacta cu invitația de acces în curând.', false, 8000);
      window.history.replaceState({}, '', '/');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWaitlist = async (
    email: string,
    setEmail: (v: string) => void,
    setLoading: (v: boolean) => void,
    setInputError: (v: boolean) => void,
  ) => {
    if (!email || !email.includes('@')) {
      setInputError(true);
      setTimeout(() => setInputError(false), 1500);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('https://api.flexara.ro/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setEmail('');
        showToast('✓ Verifică emailul pentru a confirma înscrierea.');
      } else {
        const data = await res.json();
        showToast(data.detail || 'A apărut o eroare. Încearcă din nou.', true);
      }
    } catch {
      showToast('Eroare de conexiune. Încearcă din nou.', true);
    }
    setLoading(false);
  };

  return (
    <>
      <div className="lp">

        <nav className="nav">
          <div className="logo">
            {!logoError ? (
              <img
                src="/flexara-icon.png"
                alt="Flexara"
                width={32}
                height={32}
                style={{ borderRadius: 6 }}
                onError={() => setLogoError(true)}
              />
            ) : (
              <span style={{ display: 'flex', width: 32, height: 32, borderRadius: 8, background: '#1F4E79', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                  <rect width="32" height="32" rx="8" fill="#1F4E79" />
                  <text x="7" y="22" fontFamily="serif" fontSize="18" fontWeight="bold" fill="#F39C12">F</text>
                  <rect x="18" y="8" width="3" height="3" rx="1" fill="#F39C12" opacity="0.9" />
                  <rect x="18" y="13" width="3" height="5" rx="1" fill="#F39C12" opacity="0.7" />
                  <rect x="18" y="20" width="3" height="3" rx="1" fill="#F39C12" opacity="0.5" />
                </svg>
              </span>
            )}
            <span className="logo-text">Flexara</span>
          </div>
          <span className="nav-badge">Acces prin invitație</span>
          <Link to="/access" className="nav-login">Intră în cont</Link>
        </nav>

        <section className="hero">
          <span className="hero-tag">Dureri de spate · Hernie de disc · Sciatică</span>
          <h1>Mișcare fără durere.<br /><em>Ghidaj zilnic,</em> în română.</h1>
          <p className="sub">Un plan de exerciții organizat, urmărit zilnic de AI pe baza informațiilor oferite în tracker, adaptat la situația ta. Te ajută să rămâi consecvent între vizitele la specialist.</p>
          <span className="not-medical">
            <i className="ti ti-info-circle" aria-hidden="true" />
            Aplicație de wellness · nu înlocuiește medicul sau kinetoterapeutul
          </span>
          <div className="cta-group">
            <div className="email-row">
              <input
                type="email"
                placeholder="adresa ta de email"
                value={email1}
                onChange={e => setEmail1(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleWaitlist(email1, setEmail1, setLoading1, setInputError1)}
                style={inputError1 ? { borderColor: 'rgba(220,60,60,0.5)' } : undefined}
              />
              <button
                className="btn-primary"
                disabled={loading1}
                onClick={() => handleWaitlist(email1, setEmail1, setLoading1, setInputError1)}
              >
                {loading1 ? 'Se trimite...' : 'Cere acces'}
              </button>
            </div>
            <p className="cta-note">Accesul se acordă manual prin invitație · fără card</p>
          </div>
        </section>

        <div className="section-divider" />

        <section className="problems">
          <p className="section-label">Recunoști situația?</p>
          <div className="problem-list">
            <div className="problem-item">
              <i className="ti ti-calendar-off problem-icon" aria-hidden="true" />
              <p><strong>Kinetoterapeutul îți dă exerciții</strong> — dar nu există nicio urmărire a progresului între ședințe. Rămâi singur cu o foaie de hârtie.</p>
            </div>
            <div className="problem-item">
              <i className="ti ti-video-off problem-icon" aria-hidden="true" />
              <p><strong>YouTube are mii de videouri</strong> — dar nu știi care se potrivește situației tale și e greu să urmărești un plan coerent de la o zi la alta.</p>
            </div>
            <div className="problem-item">
              <i className="ti ti-message-off problem-icon" aria-hidden="true" />
              <p><strong>Găsești răspunsuri generice</strong>, fără context despre ce ai făcut ieri sau cum te-ai simțit săptămâna trecută.</p>
            </div>
          </div>
          <p className="solution-intro">Flexara știe <em>unde ești azi</em><br />și ce urmează mâine.</p>
          <p className="solution-sub">Exercițiile sunt organizate pe faze de recuperare și selectate din literatura de specialitate. Decizia finală aparține întotdeauna ție și medicului tău.</p>
        </section>

        <div className="section-divider" />

        <section className="features">
          <p className="section-label">Ce primești</p>
          <div className="features-grid">
            <div className="feature-card">
              <i className="ti ti-robot feature-icon" aria-hidden="true" />
              <h3>Chat în română</h3>
              <p>Întreabă orice despre exercițiile tale sau progres — răspuns adaptat contextului tău, nu generic.</p>
            </div>
            <div className="feature-card">
              <i className="ti ti-list-check feature-icon" aria-hidden="true" />
              <h3>Plan zilnic</h3>
              <p>Exerciții organizate progresiv, cu instrucțiuni clare. Știi exact ce să faci în fiecare zi.</p>
            </div>
            <div className="feature-card">
              <i className="ti ti-chart-line feature-icon" aria-hidden="true" />
              <h3>Tracker zilnic</h3>
              <p>Notezi cum te simți zi de zi. AI-ul folosește aceste date pentru a adapta planul tău.</p>
            </div>
            <div className="feature-card">
              <i className="ti ti-alert-triangle feature-icon" aria-hidden="true" />
              <h3>Când să mergi la medic</h3>
              <p>Unele simptome nu trebuie amânate. Aplicația îți amintește care sunt semnele că trebuie să consulți un specialist.</p>
            </div>
          </div>
        </section>

        <div className="section-divider" />

        <div style={{ padding: '0 0 4rem' }}>
          <div className="beta-banner">
            <i className="ti ti-gift" aria-hidden="true" />
            <div className="beta-banner-text">
              <h3>Acces beta gratuit</h3>
              <p>În perioada de beta, aplicația este gratuită pentru toți utilizatorii invitați. Prețurile vor fi anunțate la lansarea oficială.</p>
            </div>
          </div>
        </div>

        <div className="medical-box">
          <i className="ti ti-shield" aria-hidden="true" />
          <p>Flexara este o aplicație de wellness — nu un dispozitiv medical și nu oferă diagnostic. Exercițiile sunt orientative. Consultați întotdeauna un medic sau kinetoterapeut înainte de a începe orice program de recuperare, în special dacă aveți simptome acute sau diagnostice confirmate.</p>
        </div>

        <section className="waitlist-bottom">
          <h2>Rezervă-ți locul</h2>
          <p>Accesul se deschide treptat. Înscrie-te și te contactăm cu invitația.</p>
          <div className="email-row-center">
            <input
              type="email"
              placeholder="email@exemplu.ro"
              value={email2}
              onChange={e => setEmail2(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleWaitlist(email2, setEmail2, setLoading2, setInputError2)}
              style={inputError2 ? { borderColor: 'rgba(220,60,60,0.5)' } : undefined}
            />
            <button
              className="btn-primary"
              disabled={loading2}
              onClick={() => handleWaitlist(email2, setEmail2, setLoading2, setInputError2)}
            >
              {loading2 ? 'Se trimite...' : 'Cere acces'}
            </button>
          </div>
        </section>

        <footer className="footer">
          <div className="footer-top">
            <span className="footer-text">© 2026 Flexara</span>
            <div className="footer-links">
              <Link to="/termeni">Termeni și condiții</Link>
              <Link to="/confidentialitate">Confidențialitate</Link>
              <a href="mailto:contact@flexara.ro">Contact</a>
            </div>
          </div>
          <div className="footer-bottom">
            <span className="footer-legal">FlexDev S.R.L. &nbsp;·&nbsp; Reg. Com. J2026023643002 &nbsp;·&nbsp; CUI 54455982</span>
          </div>
        </footer>

      </div>

      {toast.visible && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3a5c',
          border: `0.5px solid ${toast.error ? 'rgba(220,60,60,0.4)' : 'rgba(243,156,18,0.4)'}`,
          color: '#e8f0f8', padding: '14px 20px', borderRadius: 10,
          fontSize: 14, zIndex: 100,
          maxWidth: 'calc(100vw - 32px)', width: 'max-content',
          textAlign: 'center', boxSizing: 'border-box',
        }}>
          {toast.message}
        </div>
      )}
    </>
  );
}
